<template>
  <FsTooltip
    ref="accountRef"
    :teleport="false"
    placement="bottom-end"
    trigger="click"
    mobile-conventional
    show-mask
    :mobile-style="{
      top: '50px',
      right: '0',
      left: 'auto',
      width: '288px',
      boxShadow: 'none',
      height: 'calc(100dvh - 50px)',
      padding: '16px',
      background: '#fff',
      borderRadius: '0',
      transform: 'none',
    }"
    @visible-change="handleVisibleChange"
    :offset-y="11"
    :popper-content-style="{
      width: '288px',
      maxWidth: '288px',
      padding: '16px',
      borderRadius: '8px',
    }"
  >
    <a class="account_icon" href="javascript:;">
      <img :src="accountImg" alt="Account" class="iconfont_nav" />
    </a>
    <template #content>
      <h3 class="welcome">Hi,{{ userInfo?.customers_firstname }}</h3>
      <div class="account_menu">
        <a
          v-for="item in accountMenu"
          :key="item.path"
          href="javascript:;"
          @click.prevent.stop="click(item)"
          @mouseenter="isSignOutHover = true"
          @mouseleave="isSignOutHover = false"
          >{{ item.title }}</a
        >
        <span class="splitLine"></span>
        <span
          class="signOut"
          @click="signOut"
          @mouseenter="isSignOutHover = true"
          @mouseleave="isSignOutHover = false"
          >{{ t('components/layout.accountSignOut') }}</span
        >
      </div>
    </template>
  </FsTooltip>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useUserStore } from '@/store/user'
import { storeToRefs } from 'pinia'
import { handleFsLink } from '@/utils'
import { Cookie } from '@/utils'
import accountImg from '@/assets/images/user.svg?url'
import { t } from '@/lang'
interface AccountMenu {
  title: string
  path: string
}

const userStore = useUserStore()
const emit = defineEmits<{
  (e: 'open-change', visible: boolean): void
}>()

const ACCOUNT_OVERLAY_SOURCE = 'account'
const CLOSE_MOBILE_OVERLAYS_EVENT = 'close-mobile-overlays'

const accountRef = ref<{ hide: () => void } | null>(null)
const accountMenu = computed<AccountMenu[]>(() => [
  {
    title: t('components/layout.accountMyAccount'),
    path: '/my-account',
  },
  {
    title: t('components/layout.accountOrders'),
    path: '/order-history',
  },
  {
    title: t('components/layout.accountQuotes'),
    path: '/quote_history.html',
  },
])

const { userInfo } = storeToRefs(userStore)

const click = (item: AccountMenu) => {
  location.href = handleFsLink(item.path)
}

const isSignOutHover = ref(false)

/** 监听移动端遮罩点击；FsTooltip 默认会忽略自身遮罩，需要显式关闭侧滑面板。 */
const handleMaskClick = (event: MouseEvent) => {
  if ((event.target as HTMLElement)?.classList.contains('fs-tooltip__mobile--mask')) {
    accountRef.value?.hide()
  }
}

/** 打开账户抽屉时关闭目录、本页导航和语言下拉。 */
const handleOverlayClose = (event: Event) => {
  const source = (event as CustomEvent<{ source?: string }>).detail?.source
  if (source !== ACCOUNT_OVERLAY_SOURCE) accountRef.value?.hide()
}

const handleVisibleChange = (visible: boolean) => {
  emit('open-change', visible)
  if (visible) {
    document.dispatchEvent(new CustomEvent(CLOSE_MOBILE_OVERLAYS_EVENT, { detail: { source: ACCOUNT_OVERLAY_SOURCE } }))
    document.addEventListener('click', handleMaskClick, true)
  } else {
    document.removeEventListener('click', handleMaskClick, true)
  }
}

onMounted(() => document.addEventListener(CLOSE_MOBILE_OVERLAYS_EVENT, handleOverlayClose))

onBeforeUnmount(() => {
  document.removeEventListener('click', handleMaskClick, true)
  document.removeEventListener(CLOSE_MOBILE_OVERLAYS_EVENT, handleOverlayClose)
})

const signOut = () => {
  Cookie.delete('token_new')
  setTimeout(() => {
    location.href = handleFsLink('/')
  }, 0)
}
</script>
<style lang="scss" scoped>
.welcome {
  text-align: center;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e5e5;
  font-weight: 600;
  @include font14;
  color: #19191a;
}

.account_menu {
  margin-top: 8px;
  display: flex;
  flex-direction: column;

  a {
    width: 100%;
    height: 100%;
    padding: 7px;
    border-radius: 4px;
    display: flex;
    cursor: pointer;
    @include font13;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    color: #3d3d3d;
    transition: all 0.3s;
    cursor: pointer;

    &:hover {
      background: #f2f2f2;
      color: #19191a;
    }
  }

  .splitLine {
    width: 100%;
    height: 1px;
    background: #eeeeee;
    margin: 8px 0;
  }

  .signOut {
    width: 100%;
    height: 100%;
    padding: 7px;

    border-radius: 4px;
    display: flex;
    cursor: pointer;
    @include font13;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    color: #3d3d3d;
    transition: all 0.3s;
    cursor: pointer;

    &:hover {
      background: #f2f2f2;
      color: #19191a;
    }
  }
}

.account_icon {
  color: #19191a;
  position: relative;
  display: block;
  border-radius: 4px;

  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    position: absolute;
    transition: background 0.3s ease-in-out;
    z-index: 1;
    border-radius: 4px;
  }

  &:hover {
    text-decoration: none;
    background: unset;

    &::before {
      background: color-mix(in hsl, #fff 40%, transparent);
    }
  }

  .iconfont_nav {
    width: 24px;
    height: 24px;
    border-radius: 4px;
  }
}
</style>

<style lang="scss">
@media screen and (max-width: 768px) {
  .fs-tooltip__mobile--context {
    margin-right: 0 !important;
  }

  .fs-tooltip__mobile--mask {
    inset: 50px 0 0 !important;
  }

  .fs-tooltip__mobile--content {
    width: 288px;
    overflow: hidden;
    box-shadow: none;
    animation: account-slide-in 0.25s ease-out;
    padding: 20px !important;

    // 关闭改为点遮罩或再次点击头像，不再展示右上角关闭按钮。
    > .iconfont,
    .fs-icon {
      display: none;
    }

    .welcome {
      margin: 0;
      padding: 0 0 16px;
      font-size: 14px;
      line-height: 22px;
    }

    .account_menu {
      margin-top: 8px;

      a,
      .signOut {
        min-height: 40px;
        padding: 9px 8px;
        font-size: 14px;
        line-height: 22px;
      }

      .splitLine {
        margin: 8px 0;
      }
    }
  }

  @keyframes account-slide-in {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
}
</style>
